import type { MarkdownExport } from '@fos/shared/contracts/knowledge';
import { Copy, Download, FileDown } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { appErrorCode } from '../../../lib/api';
import { formatDateTimeShort } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { type Access, useAccess } from '../../../lib/useAccess';
import { FormMessage } from '../../../ui/Blocks';
import { Button, type ButtonProps } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Markdown } from '../../../ui/Markdown';
import { Segmented } from '../../../ui/Segmented';
import { ErrorState, Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { countLabel } from '../knowledge-format';

export type MarkdownExportKind = 'client' | 'project';

const FILE: Record<MarkdownExportKind, 'CLIENT.md' | 'PROJECT.md'> = { client: 'CLIENT.md', project: 'PROJECT.md' };

/** Adres pobrania (REST, sesja + „Eksportuje dane”). */
export function markdownExportUrl(kind: MarkdownExportKind, id: string): string {
  return `/api/exports/${kind}/${id}.md`;
}

/**
 * Czy pokazać „Eksportuj CLIENT.md / PROJECT.md” (tylko UI; backend i tak sprawdza): „Eksportuje dane”,
 * moduł Wiedza i moduł Klienci / Projekty.
 */
export function canExportMarkdown(access: Access, kind: MarkdownExportKind): boolean {
  return (
    access.flag('exportsData') && access.can('knowledge') && access.can(kind === 'client' ? 'clients' : 'projects')
  );
}

function useExport(kind: MarkdownExportKind, id: string, enabled: boolean) {
  const client = trpc.knowledge.exportClientMd.useQuery(
    { clientId: id },
    { enabled: enabled && kind === 'client', staleTime: 0, gcTime: 0 },
  );
  const project = trpc.knowledge.exportProjectMd.useQuery(
    { projectId: id },
    { enabled: enabled && kind === 'project', staleTime: 0, gcTime: 0 },
  );
  return kind === 'client' ? client : project;
}

/**
 * Podgląd i pobranie CLIENT.md / PROJECT.md (MVP §8, §11). Plik jest generowany z bieżących danych przy każdym
 * otwarciu (baza = źródło prawdy), z datą i listą źródeł; bez kwot bez „Widzi stawki i kwoty”, bez sekretów.
 */
export function ExportMarkdownDialog({
  kind,
  id,
  open,
  onOpenChange,
}: {
  kind: MarkdownExportKind;
  id: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const query = useExport(kind, id, open);
  const [view, setView] = useState<'preview' | 'source'>('preview');
  const file = FILE[kind];
  const data = query.data as MarkdownExport | undefined;

  const copy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.markdown);
      toast.success(`Skopiowano ${file}`);
    } catch {
      toast.error('Nie udało się skopiować. Pobierz plik.');
    }
  };

  let body: ReactNode;
  if (query.isPending) {
    body = (
      <div role="status" aria-label="Generowanie" className="flex flex-col gap-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  } else if (query.isError) {
    const code = appErrorCode(query.error);
    body =
      code === 'FORBIDDEN' ? (
        <FormMessage>Eksport wymaga uprawnienia „Eksportuje dane”. Poproś administratora.</FormMessage>
      ) : code === 'NOT_FOUND' ? (
        <FormMessage>Nie ma takiego rekordu albo nie masz do niego pełnego dostępu.</FormMessage>
      ) : (
        <ErrorState
          compact
          title={`Nie udało się wygenerować ${file}`}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      );
  } else if (data) {
    body = (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-caption text-fg-muted">
            Wygenerowano {formatDateTimeShort(data.generatedAt)} ·{' '}
            {countLabel(data.sources.length, ['źródło', 'źródła', 'źródeł'])}
          </p>
          <Segmented
            aria-label="Widok eksportu"
            size="s"
            value={view}
            onValueChange={setView}
            options={[
              { value: 'preview', label: 'Podgląd' },
              { value: 'source', label: 'Markdown' },
            ]}
          />
        </div>
        <div className="max-h-[50dvh] overflow-y-auto rounded-lg border border-line p-4">
          {view === 'preview' ? (
            <Markdown className="text-body-s">{data.markdown}</Markdown>
          ) : (
            <pre className="font-mono text-caption break-words whitespace-pre-wrap text-fg">{data.markdown}</pre>
          )}
        </div>
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={`Eksport ${file}`}
      description="Plik powstaje z bieżących danych w chwili eksportu. Zmiany wprowadzaj w aplikacji, nie w pliku."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Zamknij
          </Button>
          <Button variant="secondary" icon={Copy} disabled={!data} onClick={() => void copy()}>
            Kopiuj
          </Button>
          {data ? (
            <Button asChild variant="primary">
              <a href={markdownExportUrl(kind, id)} download={file}>
                <Download size={16} strokeWidth={1.75} aria-hidden />
                Pobierz {file}
              </a>
            </Button>
          ) : (
            <Button variant="primary" icon={Download} disabled>
              Pobierz {file}
            </Button>
          )}
        </>
      }
    >
      {body}
    </Dialog>
  );
}

/** Przycisk „Eksportuj CLIENT.md / PROJECT.md” z dialogiem podglądu (ukryty bez uprawnień). */
export function ExportMarkdownButton({
  kind,
  id,
  variant = 'ghost',
  size = 's',
  label,
}: {
  kind: MarkdownExportKind;
  id: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  label?: string;
}) {
  const access = useAccess();
  const [open, setOpen] = useState(false);
  if (!canExportMarkdown(access, kind)) return null;
  return (
    <>
      <Button variant={variant} size={size} icon={FileDown} onClick={() => setOpen(true)}>
        {label ?? `Eksportuj ${FILE[kind]}`}
      </Button>
      <ExportMarkdownDialog kind={kind} id={id} open={open} onOpenChange={setOpen} />
    </>
  );
}
